import React, { useState, useEffect } from "react";
import { PrintProduct } from "../../../CheckoutPage/components/types";
import AddToCartButton from "./AddToCartButton";
import RemoveFromCartButton from "./RemoveFromCartButton";
import ViewCartLink from "./ViewCartLink";

interface PrintLightboxFooterProps {
  photo?: any;
  eventId?: number;
  addToCart?: (photo: any) => void;
  removeFromCart?: (photo: any) => void;
  primaryColor?: string;
  hasCartItems?: boolean;
  cartId?: number;
  isEmbed?: boolean;
  lightboxPrintProductsEnabled?: boolean;
}

const PrintLightboxFooter: React.FC<PrintLightboxFooterProps> = ({
  photo,
  eventId,
  addToCart,
  removeFromCart,
  primaryColor = "#259fa5",
  hasCartItems = false,
  cartId,
  isEmbed = false,
  lightboxPrintProductsEnabled = false
}) => {
  const [printProducts, setPrintProducts] = useState<PrintProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [showProducts, setShowProducts] = useState(false);

  const handleViewPriceList = async () => {
    setShowProducts(true);
    if (printProducts.length === 0 && eventId) {
      await fetchPrintProducts();
    }
  };

  const fetchPrintProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/events/${eventId}/print_products`);
      if (response.ok) {
        const data = await response.json();
        setPrintProducts(data.printProducts || data || []);
      }
    } catch (error) {
      console.error("Error fetching print products:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | string, currency: string) => {
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;
    if (isNaN(numericPrice) || numericPrice == null) {
      return `$0.00 ${currency}`;
    }
    return `${numericPrice.toFixed(2)} ${currency}`;
  };

  const handleHidePriceList = () => {
    setShowProducts(false);
  };

  // Legacy view (when flipper flag is disabled)
  if (!lightboxPrintProductsEnabled) {
    return (
      <div className="geo-legacy-print-footer">
        <div className="geo-legacy-print-footer-text">
          {/* Header text */}
          <div className="geo-legacy-print-footer__header">
            <div className="geo-legacy-print-footer__text">
              You can <strong>customise products</strong> in Cart once you add
              this photo to cart
            </div>
          </div>

          {/* Action row */}
          <div
            className={`geo-legacy-print-footer__actions geo-btn--caps d-flex justify-content-between align-items-center ${
              showProducts ? "has-products" : ""
            }`}
          >
            <button
              onClick={showProducts ? handleHidePriceList : handleViewPriceList}
              className="geo-legacy-text-btn"
              style={{ color: primaryColor }}
            >
              {showProducts ? "Hide price list" : "View price list"}
            </button>

            {photo?.isSelected ? (
              <RemoveFromCartButton
                primaryColor={primaryColor}
                handleClick={() => {
                  removeFromCart?.(photo);
                }}
              />
            ) : (
              <AddToCartButton
                primaryColor={primaryColor}
                handleClick={() => {
                  addToCart(photo);
                }}
              />
            )}
          </div>
        </div>

        {/* Product list - only show when showProducts is true */}
        {showProducts && (
          <div className="geo-legacy-print-footer__products">
            {loading ? (
              <div className="text-center geo-small-section-loader">
                <i className="fa fa-spinner fa-spin"></i>
                <span>Loading products...</span>
              </div>
            ) : (
              <div
                className={`geo-legacy-print-footer__product-grid ${
                  printProducts.length > 2 ? "two-columns" : ""
                }`}
              >
                {printProducts.length > 2 ? (
                  <div className="d-flex">
                    {/* Left column */}
                    <div className="geo-legacy-print-footer__product-column">
                      {printProducts
                        .slice(0, Math.ceil(printProducts.length / 2))
                        .map(product => (
                          <div
                            key={product.id}
                            className="geo-legacy-print-footer__product-row d-flex align-items-center"
                          >
                            <div className="geo-legacy-print-footer__product-name">
                              {product.title}
                            </div>
                            <div className="geo-legacy-print-footer__product-dots"></div>
                            <div className="geo-legacy-print-footer__product-price">
                              {formatPrice(
                                product.totalPrice,
                                product.currency
                              )}
                            </div>
                          </div>
                        ))}
                    </div>

                    {/* Column divider */}
                    <div className="geo-legacy-print-footer__column-divider"></div>

                    {/* Right column */}
                    <div className="geo-legacy-print-footer__product-column">
                      {printProducts
                        .slice(Math.ceil(printProducts.length / 2))
                        .map(product => (
                          <div
                            key={product.id}
                            className="geo-legacy-print-footer__product-row d-flex align-items-center"
                          >
                            <div className="geo-legacy-print-footer__product-name">
                              {product.title}
                            </div>
                            <div className="geo-legacy-print-footer__product-dots"></div>
                            <div className="geo-legacy-print-footer__product-price">
                              {formatPrice(
                                product.totalPrice,
                                product.currency
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  /* Single column for 2 or fewer items */
                  printProducts.map(product => (
                    <div
                      key={product.id}
                      className="geo-legacy-print-footer__product-row d-flex align-items-center"
                    >
                      <div className="geo-legacy-print-footer__product-name">
                        {product.title}
                      </div>
                      <div className="geo-legacy-print-footer__product-dots"></div>
                      <div className="geo-legacy-print-footer__product-price">
                        {formatPrice(product.totalPrice, product.currency)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // New simple design (when flipper flag is enabled)
  return (
    <div className="geo-print-head-div">
      <div className="geo-print-head-div-text">
        {/* Header text */}
        <div className="geo-print-head-div__header">
          <div className="geo-print-head-div__text">
            Select the products that you want to buy
          </div>
        </div>

        {/* Action row - only show View Cart when there are items in cart */}
        <div>
          {hasCartItems && cartId && (
            <ViewCartLink
              cartId={cartId}
              isEmbed={isEmbed}
              parentName="print-lightbox-footer"
              primaryColor={primaryColor}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintLightboxFooter;
